/**
 * Issue #174 — Shared LP footer for the marketing landing pages.
 *
 * Used by the main LP (apps/cms/src/app/(front)/page.tsx) and the samples
 * listing (apps/cms/src/app/(front)/samples/page.tsx). The header is *not*
 * shared because the main LP has scroll-state and a mobile menu while the
 * samples page is a simpler subset.
 */
import Link from 'next/link';
import { loginHref, planListHref, samplesHref, templatesHref } from '../_constants';

type FooterLink = { href: string; label: string; external?: boolean };

// Three branches, in order:
//   1. `external: true` → raw <a target="_blank" rel="noopener noreferrer">.
//      For destinations that should open a new tab (e.g. corporate site,
//      third-party domains).
//   2. `https?://...` href without `external` → raw <a>, same tab. For
//      cross-subdomain destinations like app.daigakus.com that share user
//      trust context but cross the origin boundary (next/link can't
//      traverse origins).
//   3. Anything else → next/link for client-side nav + prefetch.
//      Internal anchors (`#faq`, `/#how`) work through next/link too:
//      Next no-ops on hash-only nav and treats `/#x` as a real navigation.
//
// CONTRACT for new entries: if you add an `https?://` href that should
// open in a new tab, set `external: true`. Otherwise the URL stays in
// the same tab cross-origin.
function FooterAnchor({ link }: { link: FooterLink }) {
  if (link.external) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer">
        {link.label}
      </a>
    );
  }
  // Cross-subdomain destinations like app.daigakus.com still need a raw
  // anchor — Next.js Link doesn't traverse origins.
  if (/^https?:\/\//i.test(link.href)) {
    return <a href={link.href}>{link.label}</a>;
  }
  return <Link href={link.href}>{link.label}</Link>;
}

// All link constants live at module scope. The two SERVICE_LINKS arrays
// are precomputed by `context` rather than reallocated per render —
// the anchor-prefix split is a binary choice, so two static arrays is
// less costly and clearer than building the array inside the component.
const SUPPORT_LINKS: readonly FooterLink[] = [
  { href: planListHref, label: '料金一覧' },
  { href: loginHref, label: 'ログインはこちら' },
  { href: '/contact', label: 'お問合せ' },
  { href: 'https://daigaku.university/', label: '運営会社', external: true },
];

const LEGAL_LINKS: readonly FooterLink[] = [
  { href: '/terms', label: '利用規約' },
  { href: '/privacy', label: 'プライバシーポリシー' },
  { href: '/tokushoho', label: '特定商取引法に基づく表記' },
  { href: '/cookies', label: 'Cookie ポリシー' },
];

const SERVICE_LINKS_BY_CONTEXT = {
  // On the LP root, anchor hrefs scroll in place: `#how`, `#cases`, etc.
  'lp-root': [
    { href: '#how', label: '使い方' },
    { href: '#cases', label: '業種別' },
    { href: templatesHref, label: 'テンプレート' },
    { href: samplesHref, label: '公開サンプル' },
    { href: '#pricing', label: '料金' },
    { href: planListHref, label: 'プラン詳細' },
    { href: '#faq', label: 'FAQ' },
  ],
  // On a subpage (e.g. /samples), anchors must navigate to the LP root
  // first: `/#how`, `/#cases`, etc.
  subpage: [
    { href: '/#how', label: '使い方' },
    { href: '/#cases', label: '業種別' },
    { href: templatesHref, label: 'テンプレート' },
    { href: samplesHref, label: '公開サンプル' },
    { href: '/#pricing', label: '料金' },
    { href: planListHref, label: 'プラン詳細' },
    { href: '/#faq', label: 'FAQ' },
  ],
} as const satisfies Record<'lp-root' | 'subpage', readonly FooterLink[]>;

type LpFooterProps = {
  /**
   * Where the footer is rendered. Determines whether in-page anchor links
   * (#how, #pricing, …) scroll in place ('lp-root') or navigate to the LP
   * root and then scroll ('subpage'). Required so each call site is explicit.
   */
  context: 'lp-root' | 'subpage';
};

export function LpFooter({ context }: LpFooterProps) {
  const SERVICE_LINKS = SERVICE_LINKS_BY_CONTEXT[context];

  return (
    <footer className="lp-footer">
      <div className="lp-footer-inner">
        <div>
          <div className="lp-footer-brand">daigakus</div>
          <p className="lp-footer-tagline">
            中小企業・お店・士業のための、Web サイトサービス。今日始めて、今日公開。
          </p>
        </div>
        <div className="lp-footer-links">
          <div className="lp-footer-col">
            <h4>サービス</h4>
            {SERVICE_LINKS.map((l) => (
              <FooterAnchor key={`${l.href}|${l.label}`} link={l} />
            ))}
          </div>
          <div className="lp-footer-col">
            <h4>サポート</h4>
            {SUPPORT_LINKS.map((l) => (
              <FooterAnchor key={`${l.href}|${l.label}`} link={l} />
            ))}
          </div>
        </div>
      </div>
      <div className="lp-footer-bottom">
        {/* Year computed per render so a long-running SSR process doesn't
            emit last year's copyright after Dec 31. LpFooter is a Server
            Component, so every request re-renders — no client hydration
            mismatch. Cost: one Date allocation per render. */}
        <span>&copy; {new Date().getFullYear()} daigakus</span>
        <div className="lp-footer-legal">
          {LEGAL_LINKS.map((l) => (
            <FooterAnchor key={`${l.href}|${l.label}`} link={l} />
          ))}
        </div>
      </div>
    </footer>
  );
}
